import { useState } from 'react'

import { Checkbox, Modal, Text } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { BACKFILL_VOE_OUTREACH } from 'src/graphql/companyVoEOutreachConfig.graphql'

interface BackfillOutreachModalProps {
  companyId: string
  opened: boolean
  onClose: () => void
}

const STAGE_OPTIONS = [
  { label: 'Lead', applicantStage: 'PROSPECT', hiringStage: 'LEAD' },
  {
    label: 'Application',
    applicantStage: 'PROSPECT',
    hiringStage: 'APPLICATION',
  },
  {
    label: 'Qualification',
    applicantStage: 'PROSPECT',
    hiringStage: 'QUALIFICATION',
  },
  {
    label: 'Onboarding',
    applicantStage: 'PROSPECT',
    hiringStage: 'ONBOARDING',
  },
  { label: 'Hired', applicantStage: 'HIRED', hiringStage: null },
] as const

const BackfillOutreachModal = ({
  companyId,
  opened,
  onClose,
}: BackfillOutreachModalProps) => {
  const [selected, setSelected] = useState<string[]>([])

  const [backfill, { loading }] = useMutation(BACKFILL_VOE_OUTREACH, {
    onCompleted: () => {
      toast(
        'Outreach is being scheduled for eligible employment verifications. It runs in the background and can take a few minutes for large lists.',
        'success'
      )
      setSelected([])
      onClose()
    },
    onError: (error) => {
      toast(`Failed to schedule outreach: ${error.message}`, 'error')
    },
  })

  const handleSubmit = () => {
    const stages = selected.map((label) => {
      const option = STAGE_OPTIONS.find((o) => o.label === label)
      return {
        applicantStage: option.applicantStage,
        ...(option.hiringStage ? { hiringStage: option.hiringStage } : {}),
      }
    })

    backfill({
      variables: {
        input: { companyId, stages },
      },
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Schedule Outreach for Existing Verifications"
      centered
    >
      <div className="flex flex-col gap-4">
        <Text size="sm" className="text-doubleNickel-gray-600">
          This will start the automated outreach schedule for existing
          employment verifications that are in a "Not Started" or "In Progress"
          status. Select which applicant stages to include.
        </Text>

        <div>
          <Text size="sm" fw={500} className="mb-2 text-doubleNickel-gray-700">
            Applicant Stages
          </Text>
          <Checkbox.Group value={selected} onChange={setSelected}>
            <div className="flex flex-col gap-2">
              {STAGE_OPTIONS.map((option) => (
                <Checkbox
                  key={option.label}
                  value={option.label}
                  label={option.label}
                />
              ))}
            </div>
          </Checkbox.Group>
        </div>

        <Text size="xs" className="text-doubleNickel-gray-400">
          Verifications that already have outreach scheduled or in progress will
          be skipped.
        </Text>

        <div className="flex justify-end gap-2">
          <DNButton variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </DNButton>
          <DNButton
            onClick={handleSubmit}
            disabled={selected.length === 0}
            loading={loading}
          >
            Schedule Outreach
          </DNButton>
        </div>
      </div>
    </Modal>
  )
}

export default BackfillOutreachModal
